import type {
    FileUploadFileDescriptionConstantType,
    FileUploadProgressSizeConstantType,
    FileUploadDescriptionConstantType,
    FileUploadValidationConstantType,
    FileUploadPlacementConstantType,
    FileUploadAnnounceConstantType,
    FileUploadValidationResult,
    FileUploadSizeConstantType,
    FileUploadIncomingFile,
    FileUploadLocalizeType,
    FileUploadStatus,
    IFileRejection,
    FileUploadRow,
    IUploadFile
} from "./type";
import type {
    AttachmentUploadState
} from "../attachment/type";
import {
    formatPercent,
    getIntlLocale,
    formatNumber,
    isolateText
} from "../../helpers/locale";

export const FILE_UPLOAD_EMPTY_FILES: Array<IUploadFile> = [];

export const FILE_UPLOAD_DEFAULT_MAX_FILES = 10;

export const FILE_UPLOAD_ANNOUNCE_PROGRESS_STEP = 50;

const FILE_SIZE_UNITS = [
    "B",
    "KB",
    "MB",
    "GB"
];

const FILE_SIZE_STEP = 1024;

const FILE_SIZE_SEPARATOR = String.fromCharCode(160);

const FILE_SIZE_FRACTION_UNIT_INDEX = 2;

const FILE_UPLOAD_OBJECT_URLS = new Set<string>();

const FILE_UPLOAD_IMAGE_EXTENSIONS = [
    ".jpeg",
    ".heic",
    ".webp",
    ".jpg",
    ".png",
    ".gif",
    ".svg"
];

const FILE_UPLOAD_WILDCARD_KEYS: Record<string, keyof NCoreUIKit.Translation> = {
    "video": "file-upload-videos",
    "audio": "file-upload-audio",
    "image": "file-upload-images"
};

export const FILE_UPLOAD_STATUS_STATES: Record<FileUploadStatus, AttachmentUploadState> = {
    processing: "processing",
    uploading: "uploading",
    error: "error",
    queued: "idle",
    done: "done"
};

const getFileSizeUnitIndex = ({
    bytes
}: {
    bytes: number;
}) => {
    let value = bytes;

    let unitIndex = 0;

    while(value >= FILE_SIZE_STEP && unitIndex < FILE_SIZE_UNITS.length - 1) {
        value /= FILE_SIZE_STEP;

        unitIndex += 1;
    }

    return unitIndex;
};

export const formatFileSize = ({
    unitIndex: unitIndexProp,
    locale,
    bytes
}: FileUploadSizeConstantType) => {
    const unitIndex = unitIndexProp ?? getFileSizeUnitIndex({
        bytes
    });

    const value = bytes / FILE_SIZE_STEP ** unitIndex;

    const number = formatNumber({
        options: {
            maximumFractionDigits: unitIndex >= FILE_SIZE_FRACTION_UNIT_INDEX ? 1 : 0
        },
        locale,
        value
    });

    return `${number}${FILE_SIZE_SEPARATOR}${FILE_SIZE_UNITS[unitIndex]}`;
};

export const formatFileProgress = ({
    loaded,
    locale,
    total
}: FileUploadProgressSizeConstantType) => {
    const unitIndex = getFileSizeUnitIndex({
        bytes: total
    });

    const loadedNumber = formatNumber({
        options: {
            maximumFractionDigits: unitIndex >= FILE_SIZE_FRACTION_UNIT_INDEX ? 1 : 0
        },
        value: loaded / FILE_SIZE_STEP ** unitIndex,
        locale
    });

    return `${loadedNumber} / ${formatFileSize({
        bytes: total,
        unitIndex,
        locale
    })}`;
};

export const getFileExtension = ({
    name
}: {
    name: string;
}) => {
    const dotIndex = name.lastIndexOf(".");

    if(dotIndex <= 0 || dotIndex === name.length - 1) {
        return "";
    }

    return name.slice(dotIndex + 1).toLowerCase();
};

export const getIsAccepted = ({
    accept,
    name,
    type
}: {
    accept?: Array<string>;
    name: string;
    type: string;
}) => {
    if(!accept?.length) {
        return true;
    }

    const lowerName = name.toLowerCase();

    const lowerType = type.toLowerCase();

    return accept.some((pattern) => {
        const rule = pattern.trim().toLowerCase();

        if(rule.startsWith(".")) {
            return lowerName.endsWith(rule);
        }

        if(rule.endsWith("/*")) {
            return lowerType.startsWith(rule.slice(0, -1));
        }

        return lowerType === rule;
    });
};

export const getIsImageAccept = ({
    accept
}: {
    accept?: Array<string>;
}) => {
    if(!accept?.length) {
        return false;
    }

    return accept.every((pattern) => {
        const rule = pattern.trim().toLowerCase();

        return rule.startsWith("image/") || FILE_UPLOAD_IMAGE_EXTENSIONS.includes(rule);
    });
};

export const getHasImageAccept = ({
    accept
}: {
    accept?: Array<string>;
}) => {
    if(!accept?.length) {
        return true;
    }

    return accept.some((pattern) => {
        const rule = pattern.trim().toLowerCase();

        return rule.startsWith("image/") || FILE_UPLOAD_IMAGE_EXTENSIONS.includes(rule);
    });
};

export const getAcceptLabels = ({
    localize,
    accept
}: {
    localize: FileUploadLocalizeType;
    accept?: Array<string>;
}) => {
    const labels = new Set<string>();

    (accept ?? []).forEach((pattern) => {
        const rule = pattern.trim().toLowerCase();

        if(rule.startsWith(".")) {
            labels.add(rule.slice(1).toUpperCase());

            return;
        }

        const [
            group = "",
            subtype = ""
        ] = rule.split("/");

        if(subtype === "*") {
            const key = FILE_UPLOAD_WILDCARD_KEYS[group];

            labels.add(key ? localize(key) : group.toUpperCase());

            return;
        }

        labels.add(subtype.split("+")[0]?.toUpperCase() ?? rule);
    });

    return Array.from(labels);
};

export const formatList = ({
    items,
    locale
}: {
    items: Array<string>;
    locale: string;
}) => {
    try {
        return new Intl.ListFormat(getIntlLocale({
            locale
        }), {
            type: "conjunction",
            style: "long"
        }).format(items);
    } catch {
        return items.join(", ");
    }
};

export const getDescriptionText = ({
    isMultiple,
    localize,
    maxFiles,
    maxSize,
    accept,
    locale
}: FileUploadDescriptionConstantType) => {
    const parts: Array<string> = [];

    const labels = getAcceptLabels({
        localize,
        accept
    });

    if(labels.length) {
        parts.push(labels.join(", "));
    }

    if(maxSize !== undefined) {
        parts.push(localize("file-upload-max-size", [
            formatFileSize({
                bytes: maxSize,
                locale
            })
        ]));
    }

    if(isMultiple) {
        parts.push(localize("file-upload-max-files", [
            formatNumber({
                value: maxFiles,
                locale
            })
        ]));
    }

    return parts.join(" · ");
};

export const createIncomingFile = (file: File): FileUploadIncomingFile => {
    return {
        name: file.name,
        size: file.size,
        type: file.type,
        file
    };
};

export const createPreviewUri = ({
    type,
    file
}: {
    type: string;
    file?: File;
}) => {
    const canPreview = typeof URL !== "undefined" && typeof URL.createObjectURL === "function";

    if(!file || !type.startsWith("image/") || !canPreview) {
        return undefined;
    }

    const uri = URL.createObjectURL(file);

    FILE_UPLOAD_OBJECT_URLS.add(uri);

    return uri;
};

export const revokePreviewUri = ({
    uri
}: {
    uri?: string;
}) => {
    if(!uri || !FILE_UPLOAD_OBJECT_URLS.has(uri)) {
        return;
    }

    FILE_UPLOAD_OBJECT_URLS.delete(uri);

    URL.revokeObjectURL(uri);
};

export const createUploadFile = ({
    index,
    item
}: {
    item: FileUploadIncomingFile;
    index: number;
}): IUploadFile => {
    return {
        key: `${Date.now().toString(36)}-${index}-${item.name}`,
        previewUri: item.previewUri ?? createPreviewUri({
            type: item.type,
            file: item.file
        }),
        status: "queued",
        name: item.name,
        size: item.size,
        file: item.file
    };
};

export const validateFiles = ({
    currentCount,
    isMultiple,
    incoming,
    localize,
    maxFiles,
    maxSize,
    accept,
    locale
}: FileUploadValidationConstantType): FileUploadValidationResult => {
    const rejections: Array<IFileRejection> = [];

    const accepted: Array<FileUploadIncomingFile> = [];

    const limit = isMultiple ? maxFiles : 1;

    const capacity = isMultiple ? Math.max(limit - currentCount, 0) : 1;

    incoming.forEach((item) => {
        const name = isolateText({
            text: item.name
        });

        const base = {
            file: item.file,
            size: item.size,
            name: item.name
        };

        if(!getIsAccepted({
            name: item.name,
            type: item.type,
            accept
        })) {
            rejections.push({
                ...base,
                message: localize("file-upload-reject-type", [
                    name,
                    formatList({
                        items: getAcceptLabels({
                            localize,
                            accept
                        }),
                        locale
                    })
                ]),
                code: "type"
            });

            return;
        }

        if(maxSize !== undefined && item.size > maxSize) {
            rejections.push({
                ...base,
                message: localize("file-upload-reject-size", [
                    name,
                    formatFileSize({
                        bytes: item.size,
                        locale
                    }),
                    formatFileSize({
                        bytes: maxSize,
                        locale
                    })
                ]),
                code: "size"
            });

            return;
        }

        if(accepted.length >= capacity) {
            rejections.push({
                ...base,
                message: localize(limit === 1 ? "file-upload-reject-count-one" : "file-upload-reject-count", [
                    name,
                    formatNumber({
                        value: limit,
                        locale
                    })
                ]),
                code: "count"
            });

            return;
        }

        accepted.push(item);
    });

    return {
        rejections,
        accepted
    };
};

export const getFileDescription = ({
    locale,
    file
}: FileUploadFileDescriptionConstantType) => {
    const status = file.status ?? "queued";

    if(status === "uploading") {
        const progress = file.progress ?? 0;

        const parts = [
            formatPercent({
                value: progress / 100,
                locale
            })
        ];

        if(file.size !== undefined) {
            parts.push(formatFileProgress({
                loaded: file.size * progress / 100,
                total: file.size,
                locale
            }));
        }

        return parts.join(" · ");
    }

    if(status !== "done") {
        return undefined;
    }

    const parts = [
        getFileExtension({
            name: file.name
        }).toUpperCase()
    ];

    if(file.size !== undefined) {
        parts.push(formatFileSize({
            bytes: file.size,
            locale
        }));
    }

    return parts.filter(Boolean).join(" · ") || undefined;
};

export const getRows = ({
    removedEntries,
    files
}: FileUploadPlacementConstantType) => {
    const rows: Array<FileUploadRow> = files.map((file) => ({
        kind: "file",
        file
    }));

    [...removedEntries].sort((first, second) => first.index - second.index).forEach((entry) => {
        rows.splice(Math.min(entry.index, rows.length), 0, {
            kind: "removed",
            entry
        });
    });

    return rows;
};

export const getUploadAnnouncement = ({
    localize,
    previous,
    locale,
    files
}: FileUploadAnnounceConstantType) => {
    const messages: Array<string> = [];

    files.forEach((file) => {
        const before = previous.get(file.key);

        const status = file.status ?? "queued";

        const progress = file.progress ?? 0;

        if(status === "uploading" && before?.status !== "uploading") {
            messages.push(localize("attachment-uploading", [
                file.name
            ]));

            return;
        }

        if(status === "uploading" && (before?.progress ?? 0) < FILE_UPLOAD_ANNOUNCE_PROGRESS_STEP && progress >= FILE_UPLOAD_ANNOUNCE_PROGRESS_STEP) {
            messages.push(localize("file-upload-announce-progress", [
                file.name,
                formatPercent({
                    value: FILE_UPLOAD_ANNOUNCE_PROGRESS_STEP / 100,
                    locale
                })
            ]));

            return;
        }

        if(status === "done" && before && before.status !== "done") {
            messages.push(localize("file-upload-announce-done", [
                file.name
            ]));

            return;
        }

        if(status === "error" && before?.status !== "error") {
            messages.push(localize("file-upload-announce-error", [
                file.name,
                file.errorText ?? localize("upload-failed")
            ]));
        }
    });

    return messages.join(" ");
};
